/**
 * CommunicationLoopsPanel — monitors pending and closed communication loops.
 */

import React, { useState, useMemo } from 'react';
import { Table, Tag, Typography, Segmented, Avatar, Tooltip, Empty, Modal, Button } from 'antd';
import { RefreshCw, CheckCircle, Clock, ArrowDownLeft, ArrowUpRight, ArrowRight, AlertTriangle, AlertCircle, Flame, Minus } from 'lucide-react';
import { LOOP_AGE_BUCKETS } from '../constants';

const { Text } = Typography;

function getInitials(email = '') {
  return email.slice(0, 2).toUpperCase();
}

function avatarColor(email) {
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

function fmtDate(date) {
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtRelative(date) {
  if (!date) return '\u2014';
  const now = new Date();
  const diffMs = now - date;
  const diffH = Math.floor(diffMs / 3600000);
  if (diffH < 1) return 'Just now';
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'Yesterday';
  if (diffD < 7) return `${diffD}d ago`;
  if (diffD < 30) return `${Math.floor(diffD / 7)}w ago`;
  return fmtDate(date);
}

function fmtAge(hours) {
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
}

function getAgeBucket(hours) {
  return LOOP_AGE_BUCKETS.find((b) => hours <= b.maxHours) || LOOP_AGE_BUCKETS[LOOP_AGE_BUCKETS.length - 1];
}

/**
 * Priority is based on age + whether it's awaiting YOUR reply.
 * - Urgent (red): yours + >3 days
 * - High (orange): yours + >1 day, or theirs + >7 days
 * - Medium (yellow): yours + <1 day, or theirs + >3 days
 * - Low (gray): theirs + <3 days
 */
function getPriority(ageHours, direction) {
  const yours = direction === 'yours';
  if (yours && ageHours > 72) return { label: 'Urgent', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', icon: Flame };
  if (yours && ageHours > 24) return { label: 'High', color: '#f97316', bg: 'rgba(249,115,22,0.08)', icon: AlertCircle };
  if (yours) return { label: 'Medium', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: AlertTriangle };
  if (!yours && ageHours > 168) return { label: 'High', color: '#f97316', bg: 'rgba(249,115,22,0.08)', icon: AlertCircle };
  if (!yours && ageHours > 72) return { label: 'Medium', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: AlertTriangle };
  return { label: 'Low', color: '#6b7280', bg: 'rgba(107,114,128,0.06)', icon: Minus };
}

const PRIORITY_ORDER = { Urgent: 0, High: 1, Medium: 2, Low: 3 };

function ContactCell({ contact, contactEmail }) {
  const nameMatch = contact?.match(/^"?([^"<]+)"?\s*</);
  const name = nameMatch ? nameMatch[1].trim() : contactEmail;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Avatar size={28} style={{ background: avatarColor(contactEmail), fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
        {getInitials(contactEmail)}
      </Avatar>
      <div style={{ minWidth: 0 }}>
        <Text strong ellipsis style={{ display: 'block', maxWidth: 150, fontSize: 13 }}>{name}</Text>
        <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{contactEmail}</Text>
      </div>
    </div>
  );
}

const pendingColumns = [
  {
    title: 'Contact',
    dataIndex: 'contact',
    key: 'contact',
    width: 200,
    render: (_, row) => <ContactCell contact={row.contact} contactEmail={row.contactEmail} />,
  },
  {
    title: 'Awaiting',
    dataIndex: 'awaitingDirection',
    key: 'awaiting',
    width: 140,
    render: (dir) => dir === 'yours' ? (
      <Tag icon={<ArrowDownLeft size={11} style={{ marginRight: 4 }} />} color="red" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>Your Reply</Tag>
    ) : (
      <Tag icon={<ArrowUpRight size={11} style={{ marginRight: 4 }} />} color="blue" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>Their Reply</Tag>
    ),
    filters: [
      { text: 'Your Reply', value: 'yours' },
      { text: 'Their Reply', value: 'theirs' },
    ],
    onFilter: (val, record) => record.awaitingDirection === val,
  },
  {
    title: 'Last Activity',
    dataIndex: 'lastDate',
    key: 'lastDate',
    width: 120,
    render: (date) => (
      <Tooltip title={fmtDate(date)}>
        <Text type="secondary" style={{ fontSize: 12 }}>{fmtRelative(date)}</Text>
      </Tooltip>
    ),
    sorter: (a, b) => (a.lastDate || 0) - (b.lastDate || 0),
  },
  {
    title: 'Priority',
    key: 'priority',
    width: 110,
    render: (_, row) => {
      const p = getPriority(row.ageHours, row.awaitingDirection);
      const Icon = p.icon;
      return (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '3px 10px',
          borderRadius: 6,
          background: p.bg,
          border: `1px solid ${p.color}20`,
        }}>
          <Icon size={12} color={p.color} />
          <span style={{ fontSize: 11, fontWeight: 600, color: p.color }}>{p.label}</span>
        </div>
      );
    },
    sorter: (a, b) => {
      const pa = getPriority(a.ageHours, a.awaitingDirection);
      const pb = getPriority(b.ageHours, b.awaitingDirection);
      return PRIORITY_ORDER[pa.label] - PRIORITY_ORDER[pb.label];
    },
    defaultSortOrder: 'ascend',
  },
];

const closedColumns = [
  {
    title: 'Contact',
    dataIndex: 'contact',
    key: 'contact',
    width: 200,
    render: (_, row) => <ContactCell contact={row.contact} contactEmail={row.contactEmail} />,
  },
  {
    title: 'Duration',
    dataIndex: 'durationHours',
    key: 'duration',
    width: 110,
    render: (hours) => <Text type="secondary" style={{ fontSize: 13 }}>{fmtAge(hours)}</Text>,
    sorter: (a, b) => a.durationHours - b.durationHours,
  },
  {
    title: 'Closed',
    dataIndex: 'closedAt',
    key: 'closedAt',
    width: 120,
    render: (date) => (
      <Tooltip title={fmtDate(date)}>
        <Text type="secondary" style={{ fontSize: 12 }}>{fmtRelative(date)}</Text>
      </Tooltip>
    ),
    sorter: (a, b) => (a.closedAt || 0) - (b.closedAt || 0),
    defaultSortOrder: 'descend',
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: () => <Tag color="green" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>Closed</Tag>,
  },
];

export function CommunicationLoopsPanel({ metrics, isDark, externalOpen, onExternalClose }) {
  const [view, setView] = useState('pending');
  const [internalOpen, setInternalOpen] = useState(false);
  const modalOpen = externalOpen || internalOpen;
  const closeModal = () => { setInternalOpen(false); if (onExternalClose) onExternalClose(); };

  // ── Body preview disabled — privacy: metadata-only access ────────────────
  // const [bodyLoop, setBodyLoop] = useState(null);
  // const [bodyData, setBodyData] = useState(null);
  // const [bodyLoading, setBodyLoading] = useState(false);
  // const [bodyError, setBodyError] = useState(null);
  // const [bodyView, setBodyView] = useState('plain');
  //
  // useEffect(() => {
  //   if (!bodyLoop) return;
  //   if (!token) {
  //     setBodyError('Gmail token unavailable — reconnect to view email bodies.');
  //     return;
  //   }
  //   if (!bodyLoop.lastMessageId) {
  //     setBodyError('No message ID available for this thread.');
  //     return;
  //   }
  //   let cancelled = false;
  //   setBodyLoading(true);
  //   setBodyError(null);
  //   setBodyData(null);
  //   fetchMessageBody(token, bodyLoop.lastMessageId)
  //     .then((data) => {
  //       if (cancelled) return;
  //       setBodyData(data);
  //       setBodyView(data.plain ? 'plain' : (data.html ? 'html' : 'plain'));
  //     })
  //     .catch((err) => {
  //       if (cancelled) return;
  //       setBodyError(err.message || 'Failed to fetch email body.');
  //     })
  //     .finally(() => {
  //       if (!cancelled) setBodyLoading(false);
  //     });
  //   return () => { cancelled = true; };
  // }, [bodyLoop, token]);
  //
  // const closeBodyModal = () => {
  //   setBodyLoop(null);
  //   setBodyData(null);
  //   setBodyError(null);
  //   setBodyLoading(false);
  // };
  //
  // const onRowClick = (record) => ({
  //   onClick: () => setBodyLoop(record),
  //   style: { cursor: 'pointer' },
  // });

  const { pendingLoops = [], closedLoops = [], avgLoopClosureHours } = metrics;

  const awaitingYours = useMemo(() => pendingLoops.filter((l) => l.awaitingDirection === 'yours').length, [pendingLoops]);
  const awaitingTheirs = useMemo(() => pendingLoops.filter((l) => l.awaitingDirection === 'theirs').length, [pendingLoops]);

  const currentData = view === 'pending' ? pendingLoops : closedLoops;
  const currentColumns = view === 'pending' ? pendingColumns : closedColumns;
  const preview = currentData.slice(0, 5);
  const hasMore = currentData.length > 5;

  return (
    <>
      <div style={{
        borderRadius: 14,
        background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
        boxShadow: isDark
          ? '0 1px 3px rgba(0,0,0,0.2)'
          : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        padding: '20px',
        marginBottom: 24,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: isDark ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RefreshCw size={16} color="#8b5cf6" strokeWidth={2} />
            </div>
            <Text strong style={{ fontSize: 14 }}>Communication Loops</Text>
            <Tag color="purple" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}>{pendingLoops.length} pending</Tag>
            <Tag color="green" style={{ borderRadius: 6, fontWeight: 600, fontSize: 11, margin: 0 }}>{closedLoops.length} closed</Tag>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {avgLoopClosureHours !== null && (
              <Tooltip title="Average time to close a communication loop">
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={13} color="#8b5cf6" />
                  <Text type="secondary" style={{ fontSize: 12 }}>Avg closure: <strong>{fmtAge(avgLoopClosureHours)}</strong></Text>
                </div>
              </Tooltip>
            )}
            <Segmented
              size="small"
              options={[
                { label: `Pending (${pendingLoops.length})`, value: 'pending' },
                { label: `Closed (${closedLoops.length})`, value: 'closed' },
              ]}
              value={view}
              onChange={setView}
            />
          </div>
        </div>

        {/* Summary badges for pending view */}
        {view === 'pending' && pendingLoops.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{
              padding: '8px 14px', borderRadius: 10,
              background: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)',
              border: `1px solid ${isDark ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.12)'}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <ArrowDownLeft size={14} color="#ef4444" />
              <Text style={{ fontSize: 12 }}><strong>{awaitingYours}</strong> awaiting your reply</Text>
            </div>
            <div style={{
              padding: '8px 14px', borderRadius: 10,
              background: isDark ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)',
              border: `1px solid ${isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.12)'}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <ArrowUpRight size={14} color="#3b82f6" />
              <Text style={{ fontSize: 12 }}><strong>{awaitingTheirs}</strong> awaiting their reply</Text>
            </div>
          </div>
        )}

        {currentData.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              view === 'pending'
                ? 'No pending communication loops — all caught up!'
                : 'No closed loops found in this date range.'
            }
          />
        ) : (
          <>
            <Table
              dataSource={preview.map((d, i) => ({ ...d, key: i }))}
              columns={currentColumns}
              pagination={false}
              size="small"
              showHeader
            />
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <Button
                  type="text"
                  onClick={() => setInternalOpen(true)}
                  style={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: '#8b5cf6',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  View all {currentData.length} {view} loops
                  <ArrowRight size={13} />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onCancel={closeModal}
        footer={null}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {view === 'pending' ? <RefreshCw size={16} color="#8b5cf6" /> : <CheckCircle size={16} color="#10b981" />}
            <span>{view === 'pending' ? 'All Pending Loops' : 'All Closed Loops'}</span>
            <Tag color={view === 'pending' ? 'purple' : 'green'} style={{ borderRadius: 6, fontWeight: 600, fontSize: 11 }}>{currentData.length}</Tag>
          </div>
        }
        width={800}
        styles={{ body: { padding: '16px 0 0' } }}
      >
        <Table
          dataSource={currentData.map((d, i) => ({ ...d, key: i }))}
          columns={currentColumns}
          pagination={{ pageSize: 15, size: 'small', showSizeChanger: false }}
          size="small"
          scroll={{ y: 420 }}
        />
      </Modal>
    </>
  );
}
